<script setup lang="ts">
    import { reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { listRenewals, renew, renewAll, Renewal } from './Billing.access';

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const years: number[] = [];
    for (let y = currentYear + 1; y >= 2017; y--) years.push(y);

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    const state = reactive<{
        year: number;
        month: number;
        renewals: Renewal[];
        loading: boolean;
    }>({
        year: currentYear,
        month: currentMonth,
        renewals: [],
        loading: false,
    });

    function toDateInputValue(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toISOString().slice(0, 10);
    }

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    function isReadyToSend(renewal: Renewal): boolean {
        if (!renewal.ReadyToSend) return false;
        if (renewal.Type === 'PaperInvoice' && renewal.MissingInfo?.includes('Adress')) return false;
        if (renewal.Type === 'Invoice' && renewal.MissingInfo?.includes('Email')) return false;
        return true;
    }

    async function fetchRenewals(): Promise<void> {
        state.loading = true;
        try {
            state.renewals = await listRenewals(state.year, state.month);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load renewals' });
        } finally {
            state.loading = false;
        }
    }

    async function renewOne(renewal: Renewal): Promise<void> {
        try {
            await renew(renewal.ServiceSubscriptionId, renewal.PeriodFrom, renewal.PeriodTo);
            state.renewals = state.renewals.filter((r) => r.ServiceSubscriptionId !== renewal.ServiceSubscriptionId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: `${renewal.CompanyName} is marked for renewal` });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: `${renewal.CompanyName} could not be renewed` });
        }
    }

    async function renewAllRenewals(): Promise<void> {
        if (!confirm(`Renew all ${state.renewals.length} renewals for ${state.year}-${state.month}?`)) return;
        try {
            await renewAll(state.year, state.month);
            state.renewals = [];
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Period is marked for renewal' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Period could not be renewed' });
        }
    }
</script>

<template>
    <div class="d-flex flex-wrap align-items-end row">
        <div class="col-md-2">
            <div class="form-group">
                <label>Year</label>
                <select :value="state.year" class="form-control" @change="state.year = Number(($event.target as HTMLSelectElement).value)">
                    <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Month</label>
                <select :value="state.month" class="form-control" @change="state.month = Number(($event.target as HTMLSelectElement).value)">
                    <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <button type="button" class="btn btn-primary" :disabled="state.loading" @click="fetchRenewals">Fetch</button>
            </div>
        </div>
        <div class="col-md-6">
            <div class="form-group text-right">
                <button
                    v-if="state.renewals.length > 0"
                    type="button"
                    class="btn btn-warning"
                    :disabled="state.loading"
                    @click="renewAllRenewals">
                    Renew all ({{ state.renewals.length }})
                </button>
            </div>
        </div>
    </div>

    <div v-if="state.loading">Loading...</div>

    <div v-if="state.renewals.length > 0" class="table-responsive">
        <table class="table table-hover table-condensed">
            <thead class="small">
                <tr>
                    <th>Company</th>
                    <th>Paid until</th>
                    <th>Invoiced until</th>
                    <th>Billing end</th>
                    <th class="text-right">Amount</th>
                    <th>Period start</th>
                    <th>Period end</th>
                    <th class="text-right">Interval</th>
                    <th>Payment method</th>
                    <th>Missing info</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="renewal in state.renewals" :key="renewal.ServiceSubscriptionId">
                    <td>
                        {{ renewal.CompanyName }}
                        <i v-if="renewal.InvoicingInstructions" class="fa fa-exclamation-triangle" :title="renewal.InvoicingInstructions"></i>
                    </td>
                    <td>{{ formatDate(renewal.RenewFrom) }}</td>
                    <td>{{ formatDate(renewal.InvoicedUntil) }}</td>
                    <td>{{ formatDate(renewal.BillingEnd) }}</td>
                    <td class="text-right">{{ renewal.Amount }} {{ renewal.CurrencyCode }}</td>
                    <td>
                        <input
                            type="date"
                            class="form-control"
                            :value="toDateInputValue(renewal.PeriodFrom)"
                            @change="renewal.PeriodFrom = ($event.target as HTMLInputElement).value" />
                    </td>
                    <td>
                        <input
                            type="date"
                            class="form-control"
                            :value="toDateInputValue(renewal.PeriodTo)"
                            @change="renewal.PeriodTo = ($event.target as HTMLInputElement).value" />
                    </td>
                    <td class="text-right">{{ renewal.RenewalPeriod }}</td>
                    <td>{{ renewal.Type }}</td>
                    <td>{{ renewal.MissingInfo }}</td>
                    <td>
                        <button
                            type="button"
                            class="btn btn-xs"
                            :class="isReadyToSend(renewal) ? 'btn-primary' : 'btn-warning'"
                            :title="isReadyToSend(renewal) ? 'Ready' : 'Information is missing'"
                            @click="renewOne(renewal)">
                            Renew
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script lang="ts">
    export default { name: 'BillingRenewals' };
</script>

<style></style>
